import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { setMeta } from './db.js';
import {
  ZERO, add, addDays, anchorForDayOfMonth, computeAvailable, computeCycleOutlook, computeVerdict,
  confirmed, diagnose, formatMoney, kop, lastOccurrenceUpTo, nextIncomeDate,
  nextOccurrenceInclusive, prevOccurrenceInclusive, rhythmText, scheduleLabel, streamFingerprint,
  computePlan, modeChoice, DEFAULT_MODE, MODE_TITLE, type PlanMode,
  DEFAULT_REPEAT, NEVER_ENDS, type RepeatKey,
  share, sub, unknownAmount, valueOr,
  type Account, type Amount, type AvailableResult, type CreditTerms, type Goal, type IncomeStream,
  type Loan, type Obligation, type PaymentSchedule,
  type Money, type PlainDate, type VerdictResult,
} from '@skoka/core';
import { advisorContext, type AdvisorAnswer } from '@skoka/core';
import { runAdvisor, type ClarifyTurn } from './advisor.js';
import { chatAnswerOf } from './chat/answer.js';
import { tidy } from './chat/chat-text.js';
import { AnswerBody } from './chat/chat-view.js';
import { AmountInput } from './amount.js';
import { dayMonthYear, plural, pluralWord } from './text.js';
import { PencilIcon } from './sheet.js';
import {
  DebtSheet, draftLoanFields, loanDraftDetail, loanPayDate, newLoan, paysMonthly, saveDraftLoan,
  type DLoan,
} from './debt-sheet.js';
import { Tip } from './tip.js';
import { scheduleOf, type ScheduleDraft } from './schedule-fields.js';
import {
  addAccount, addExpectedIncome, addGoal, addIncome, addLoan, addObligation,
  decideInstance,
  formatAmountInput, markTrackingStarted, parseAmountInput,
  setBaselineAmounts, setPlanMode, settleObligation, todayPd,
  loadDraft, saveDraft, clearDraft, recordConsent, parseRateInput,
  loanSchedule, upsertLoanPayment, setReserve,
} from './store.js';
import { DOC_VERSION } from './documents.js';
import { Wordmark } from './logo.js';
import { Icon } from './icon.js';
import { AccountGlyph, IconGlyph } from './account-glyph.js';
import { ShotPlate, ShotReading, ShotTile } from './bank-shots-ui.js';
import { bankName, collect, listRu, loanMissing, mergeLoan, readBankShots, sameAccountName, termsOnly, type FoundLoan } from './bank-shots.js';
import { track } from './track.js';
import { payAccounts } from './account-groups.js';
import { IncomeSheet, draftRhythm, newIncome, parseDays, type IncomeDraft } from './income-form.js';
import { AccountSheet, createAccount, liquidOf, newAccount, type AccKind, type AccountDraft } from './account-form.js';
import { ObligationSheet, PRESETS, VARIABLE_PRESETS } from './obligation-form.js';
import { GoalSheet, newGoal, type GoalDraft } from './goal-form.js';
import { defaultGoalAccount, savingAccounts } from './goal-account.js';

/**
 * Онбординг на формах (PRD §4).
 *
 * Пять блоков: доход → счета → обязательства → цели → вердикт. Первая цифра
 * показывается уже после второго блока — заведомо завышенной, с честной
 * плашкой точности: ранняя награда важнее точности, но врать нельзя.
 *
 * Шаги-списки устроены одинаково: пустой экран показывает только чипсы, тап по
 * чипсу открывает модальный лист, заполненная запись встаёт в компактный
 * список сверху, чипсы остаются под ним. Правка — тап по всей строке, удаление
 * живёт внизу того же листа.
 *
 * Так сделано потому, что раскрытые формы прямо на экране не складываются: пять
 * счетов или платежей превращали шаг в длинную прокрутку, где не видно ни
 * списка целиком, ни кнопки «Дальше». Компактный список читается с одного
 * взгляда, а вся сложность ввода уезжает в лист — включая шесть полей
 * кредитки, которые раньше стояли стеной посреди шага.
 *
 * Разговорный слой поверх этих же форм появится позже. Формы — не заглушка,
 * а подложка: при недоступности модели онбординг обязан доходить до конца
 * (PRD §4.1/П4).
 */

type Step = 'income' | 'accounts' | 'firstNumber' | 'obligations' | 'baseline' | 'goals' | 'verdict';
const STEP_ORDER: Step[] = ['income', 'accounts', 'firstNumber', 'obligations', 'baseline', 'goals', 'verdict'];

/**
 * Какие поля записи заполнены: «name:1,amount:1,day:0». Только имена полей
 * из кода и признак заполненности — значения в отчёт не уходят.
 */
function filledFields(value: object): string {
  return Object.entries(value)
    .filter(([, v]) => typeof v === 'string' || typeof v === 'boolean' || typeof v === 'number')
    .map(([k, v]) => `${k}:${(typeof v === 'string' ? v.trim() !== '' : Boolean(v)) ? 1 : 0}`)
    .slice(0, 14)
    .join(',');
}

/**
 * Имя по умолчанию для новой карты.
 *
 * Вторая «Кредитка» в списке неотличима от первой, а долг у них разный —
 * поэтому со второй идёт номер. Переименовать её человек может тут же.
 */
const cardName = (accounts: DAccount[]): string => {
  const n = accounts.filter((a) => a.kind === 'credit_card').length;
  return n === 0 ? 'Кредитка' : `Кредитка ${n + 1}`;
};

/** Имя нового счёта по виду: первая карта — «Основная карта». */
const onbAccountName = (kind: AccKind, accounts: DAccount[]): string =>
  kind === 'credit_card' ? cardName(accounts)
    : kind === 'debit' ? (accounts.some((a) => a.kind === 'debit') ? 'Карта' : 'Основная карта')
      : kind === 'cash' ? 'Наличные' : 'Накопительный';


/** Доход в черновике — общий с приложением (`income-form.tsx`). */
type DIncome = IncomeDraft;

/**
 * Обязательный платёж в черновике.
 *
 * `period` появился потому, что «каждый месяц по числу» — не описание жизни:
 * няня бывает раз в неделю, страховка раз в год, а взнос за садик раз в
 * квартал. Годовая страховка, посчитанная ежемесячной, отнимает у человека
 * десятки тысяч рублей в каждом месяце — и все они неправда.
 */
interface DObligation {
  name: string; amount: string; day: string; variable: boolean;
  /**
   * Дата платежа, ритм и окончание.
   *
   * Одна модель с приложением (`repeat.ts`): списки периодичности в формах
   * успели разойтись, а разового платежа не было нигде — налог к 1 декабря
   * заводился ежемесячным и вычитался двенадцать раз в год.
   */
  sched?: ScheduleDraft;
  /** С какого счёта — черновой ссылкой `a2`; нет — первый тратимый. */
  accountId?: string;
}

const isDate = (v: string | undefined): boolean => /^\d{4}-\d{2}-\d{2}$/.test(v ?? '');

/**
 * Расписание платежа из черновика.
 *
 * Названная человеком дата главнее числа месяца: он её и назвал. Опорная дата
 * для месячных ритмов сохраняет **исходное число** — платёж 31-го не должен
 * после февраля навсегда переехать на 28-е.
 */
function draftSchedule(o: DObligation, today: PlainDate): PaymentSchedule {
  return scheduleOf(draftRepeat(o, today));
}

/**
 * Ритм черновика, включая черновики прежней версии.
 *
 * До 17.09.2026 периодичность лежала в `period`, а дата первого платежа — в
 * `first`. Человек, не дошедший до конца настройки, вернётся в неё уже на
 * новой сборке: прочитать старые поля и промолчать значило бы тихо
 * превратить его годовую страховку в ежемесячную — то есть отнять у него
 * десятки тысяч в каждом месяце, ничего не спросив.
 */
function draftRepeat(o: DObligation, today: PlainDate): ScheduleDraft {
  if (o.sched) return o.sched;

  const legacy = o as DObligation & { period?: string; first?: string };
  const repeat = LEGACY_PERIOD[legacy.period ?? 'month'] ?? DEFAULT_REPEAT;
  const date = isDate(legacy.first)
    ? (legacy.first as PlainDate)
    : anchorForDayOfMonth(dayNum(o.day), today);
  return { date, repeat, end: NEVER_ENDS };
}

/** Прежние названия периодичности. Совпадают с нынешними всюду, кроме года. */
const LEGACY_PERIOD: Record<string, RepeatKey> = {
  month: 'month', week: 'week', twoWeeks: 'twoWeeks',
  quarter: 'quarter', halfYear: 'halfYear', year: 'year',
};
/** Цель в черновике — общая с приложением (`goal-form.tsx`). */
type DGoal = GoalDraft;
/** Счёт в черновике — общий с приложением (`account-form.tsx`). */
type DAccount = AccountDraft;

export interface Draft {
  incomes: DIncome[];
  accounts: DAccount[];
  obligations: DObligation[];
  loans: DLoan[];
  needBaseline: string;
  wantBaseline: string;
  goals: DGoal[];
  /**
   * Платежи, у которых срок в этом цикле уже прошёл, а человек снял галочку
   * «оплачено». Хранятся именно снятые: по умолчанию всё отмечено, потому что
   * остатки на счетах человек называл уже после этих списаний.
   */
  stillUnpaid: number[];
  /** То же для кредитов: индексы `loans`, чей прошедший платёж не оплачен (03.11.2026). */
  loansUnpaid?: number[];
  /**
   * Как делить свободный остаток. Пусто — прежний «Баланс».
   *
   * Спрашивается на финале: выбор влияет на деньги, и человек должен сделать
   * его сам, а не обнаружить, что приложение выбрало за него.
   */
  planMode?: PlanMode;
  /**
   * О чём человек прямо сказал «не знаю».
   *
   * Не то же самое, что пустое поле: пустое — «ещё не спрашивали», а это —
   * ответ. Ноль отсюда не следует, и расчёт обязан отвечать «недостаточно
   * данных», а не подставлять нули за человека.
   */
  unknown: ('income' | 'needBaseline' | 'wantBaseline')[];
  /** На вопрос об обязательствах уже ответили, в том числе «их нет». */
  obligationsKnown?: boolean;
}

export const EMPTY_DRAFT: Draft = {
  incomes: [], accounts: [], obligations: [], loans: [],
  needBaseline: '', wantBaseline: '', goals: [], stillUnpaid: [], unknown: [],
};

const newObligation = (name: string, variable = false): DObligation => ({
  name, amount: '', day: '10', variable,
});

/**
 * Долги черновика в том виде, в каком их понимают движки.
 *
 * Кредитка и кредит здесь уже неразличимы — это и есть смысл общей сущности:
 * очередь гашения считается по ставке и остатку, а не по тому, карта это или
 * ипотека. Идентификаторы черновые и совпадают с `DGoal.debtRef`.
 */
const draftLoans = (d: Draft): Loan[] => [
  ...d.accounts
    .map((a, i) => ({ a, i }))
    .filter(({ a }) => a.kind === 'credit_card' && a.name.trim())
    .map(({ a, i }) => ({
      id: `card:${i}`, name: a.name.trim(), kind: 'credit_card' as const,
      principal: parseAmountInput(a.balance),
      ratePct: Number(a.rate.replace(',', '.')) || 0,
      payment: ZERO, paymentDay: dayNum(a.payDay), allowsEarly: true,
      card: {
        accountId: `a${i}`, limit: parseAmountInput(a.limit), statementDebt: parseAmountInput(a.needGrace) > ZERO
          ? parseAmountInput(a.needGrace) : parseAmountInput(a.balance),
        graceDays: Math.max(0, Math.trunc(Number(a.grace) || 0)), reserved: ZERO, minPaymentFloor: ZERO,
      },
    })),
  ...d.loans
    .map((l, i) => ({ l, i }))
    .filter(({ l }) => l.name.trim())
    .map(({ l, i }) => ({
      // Те же поля, что получит долг при сохранении (`draftLoanFields`):
      // предпросмотр и сохранённое не должны расходиться.
      ...draftLoanFields(l, todayPd()).fields,
      id: `loan:${i}`,
      ...(paysMonthly(l) ? { obligationId: `l${i}` } : {}),
    })),
];


/** Платежи по кредитам как обязательства: для планирования это одно и то же. */
const loanObligations = (d: Draft, accountId: string, today: PlainDate): Obligation[] =>
  d.loans
    // Личный долг, который вернут разом, платежа не имеет.
    .filter((l) => l.name.trim() && paysMonthly(l) && parseAmountInput(l.payment) > ZERO)
    // Платёж с тем же названием уже есть среди платежей: при сохранении кредит
    // возьмёт его (`upsertLoanPayment`), а не заведёт второй. Предпросмотр
    // считает так же, иначе вердикт вычел бы одну ипотеку дважды.
    .filter((l) => !d.obligations.some((o) =>
      o.name.trim().toLowerCase() === l.name.trim().toLowerCase() && money(o.amount) > ZERO))
    .map((l, i) => ({ l, i }))
    .map(({ l, i }) => ({
      id: `l${i}`, name: l.name.trim(), kind: 'loan' as const,
      amountType: 'fixed' as const, amount: parseAmountInput(l.payment),
      dayOfMonth: dayNum(l.day),
      // То же расписание, что получит платёж после сохранения: с даты платежа
      // и до конца срока.
      schedule: loanSchedule(loanPayDate(l, today), isDate(l.endDate) ? l.endDate as PlainDate : undefined),
      shiftRule: 'exact' as const,
      accountId, part: 'need' as const, isActive: true,
      ...(loanPassed(d, l, today) && (d.loansUnpaid ?? []).includes(d.loans.indexOf(l))
        ? { payments: [{ scheduledDate: loanPayDate(l, today), paid: ZERO, txIds: [], decision: 'unpaid' as const, requiredAt: parseAmountInput(l.payment) }] }
        : {}),
    }));

/**
 * Черновик глазами движка «Доступно потратить».
 *
 * Нужен, чтобы вердикт показал ту же цифру, которую человек увидит на главной
 * сразу после сохранения. Считать её здесь вторым способом было бы обманом:
 * две формулы одного и того же неизбежно разойдутся, и человек решит, что
 * приложение передумало.
 *
 * Платежи, отмеченные как уже оплаченные, помечаются подтверждёнными — иначе
 * предпросмотр покажет минус, которого после сохранения не будет.
 */
function draftAvailable(d: Draft, today: PlainDate): AvailableResult {
  const e = draftEntities(d, today);
  return computeAvailable({
    today,
    // Учёт начнётся в день сохранения — как и на главной: прошедшие платежи без
    // решения считаются оплаченными мимо приложения (03.11.2026). Без этого
    // финал вычитал прошедший платёж по кредиту, а главная — нет.
    trackingStartedAt: today,
    loans: e.loans,
    accounts: e.accounts,
    creditTerms: e.creditTerms,
    incomeStreams: e.incomeStreams,
    obligations: e.obligations,
    goals: e.goals,
    plannedExpenses: [],
    reserves: [],
  });
}

/** Черновик, переведённый в сущности ядра. */
export interface DraftEntities {
  accounts: Account[];
  creditTerms: CreditTerms[];
  incomeStreams: IncomeStream[];
  obligations: Obligation[];
  goals: Goal[];
  loans: Loan[];
  /** Счёт, с которого живут: на него ложатся приходы и платежи. */
  spendable: string;
  obligationsKnown?: boolean;
  /** Обычные траты со статусом известности: «не знаю» — это не ноль. */
  baselines: { need: Amount; want: Amount };
}

/**
 * Черновик глазами ядра.
 *
 * Одно место перевода на все ответы: и «сколько можно потратить», и вердикт,
 * и главная задача, и разбор ИИ считают по одним и тем же сущностям. Раньше
 * перевод был написан трижды, и три копии уже разошлись — расписания попадали
 * только в одну из них, и вердикт считал годовую страховку ежемесячной.
 *
 * Платежи, отмеченные как уже оплаченные, помечаются подтверждёнными: иначе
 * предпросмотр покажет минус, которого после сохранения не будет.
 */
export function draftEntities(d: Draft, today: PlainDate): DraftEntities {
  // Номер счёта — его место в черновике, как у карт ниже и у ссылок
  // «С какого счёта» и «Где копим»: прежде номер шёл по списку без
  // безымянных, и ссылки разъезжались.
  const accounts: Account[] = d.accounts
    .map((a, i) => ({ a, i }))
    .filter(({ a }) => a.name.trim())
    .map(({ a, i }) => ({
      id: `a${i}`,
      name: a.name.trim(),
      kind: a.kind,
      balance: a.kind === 'credit_card' ? (-money(a.balance) as Money) : money(a.balance),
      // «Тратим отсюда» — как назвал человек; по умолчанию накопительный не для трат.
      isLiquid: liquidOf(a),
      includeInNetWorth: true,
    }));
  // Карт может быть несколько: у людей с двумя кредитками картина долга
  // складывается именно на входе, и одна из них не должна пропасть.
  const cards = d.accounts
    .map((a, i) => ({ a, i }))
    .filter(({ a }) => a.kind === 'credit_card' && a.name.trim());
  const paid = new Map(passedThisCycle(d, today).map((x) => [x.index, x.due]));
  const spendable = accounts.find((a) => a.isLiquid)?.id ?? accounts[0]?.id ?? 'a0';
  /** Названный счёт, если он есть в черновике; иначе первый тратимый. */
  const accountRef = (ref: string | undefined): string => (ref && accounts.some((a) => a.id === ref) ? ref : spendable);

  return {
    ...(d.obligationsKnown === true ? { obligationsKnown: true } : {}),
    accounts,
    spendable,
    loans: draftLoans(d),
    creditTerms: cards.map(({ a, i }) => ({
      accountId: `a${i}`,
      limit: money(a.limit),
      statementDebt: money(a.needGrace) > ZERO ? money(a.needGrace) : money(a.balance),
      reserved: money(a.reserved ?? ''),
      paymentDay: dayNum(a.payDay),
      graceDays: Math.max(0, Math.trunc(Number(a.grace) || 0)),
    })),
    incomeStreams: d.incomes
      .filter((inc) => money(inc.amount) > ZERO)
      .map((inc, i) => ({
        id: `i${i}`, name: inc.name.trim() || 'Приход', accountId: spendable,
        amount: money(inc.amount), isVariable: inc.variable,
        dayOfMonth: incomeDay(inc), shiftRule: 'previous_business_day' as const, isActive: true,
      })),
    obligations: [
      ...d.obligations
        .map((o, i) => ({ o, i }))
        .filter(({ o }) => money(o.amount) > ZERO && o.name.trim())
        .map(({ o, i }) => ({
          id: `o${i}`, name: o.name.trim(), kind: 'other' as const,
          amountType: o.variable ? ('variable' as const) : ('fixed' as const),
          amount: money(o.amount), dayOfMonth: dayNum(o.day),
          // Расписание — часть платежа, а не украшение: без него квартальный
          // взнос считался бы ежемесячным в каждом расчёте.
          schedule: draftSchedule(o, today),
          shiftRule: 'exact' as const, accountId: accountRef(o.accountId), part: 'need' as const, isActive: true,
          ...(paid.has(i)
            ? { payments: [d.stillUnpaid.includes(i)
              // Не оплачен — явное решение, как после сохранения (`decideInstance`):
              // расчёт идёт с начала учёта, и без решения платёж счёлся бы оплаченным.
              ? { scheduledDate: paid.get(i)!, paid: ZERO, txIds: [], decision: 'unpaid' as const, requiredAt: money(o.amount) }
              : { scheduledDate: paid.get(i)!, paid: money(o.amount), txIds: [] }] }
            : {}),
        })),
      ...loanObligations(d, spendable, today),
    ],
    goals: d.goals
      .filter((g) => money(g.target) > ZERO && g.name.trim())
      .map((g, i) => ({
        id: `g${i}`, name: g.name.trim(), kind: g.kind,
        targetAmount: money(g.target), currentAmount: money(g.current),
        ...(/^\d{4}-\d{2}-\d{2}$/.test(g.date) ? { targetDate: g.date as PlainDate } : {}),
        startDate: draftGoalStart(g.start, d, today),
        ...(g.debtRef ? { loanId: g.debtRef } : {}),
        ...(g.accountId && accounts.some((a) => a.id === g.accountId) ? { accountId: g.accountId } : {}),
        // Приоритет берётся выбранный, а не выведенный из вида цели: иначе
        // выбор в листе ни на что не влияет.
        priority: g.priority,
        monthlyContribution: money(g.monthly),
        /*
          Названный взнос — договор, неназванный — «сколько останется».

          Здесь всегда стояло `fixed`, и цель без суммы получалась
          фиксированной на ноль рублей: она не просила ничего и ничего не
          принимала, хотя лист прямо обещает «посчитаю, когда закроется на
          свободных деньгах». Так же считают `addGoal` и редактор цели.
        */
        contributionMode: money(g.monthly) > ZERO ? 'fixed' as const : 'residual' as const,
        fundedInPeriod: false, status: 'active' as const,
      })),
    baselines: draftBaselines(d),
  };
}

/**
 * Обычные траты черновика со статусом известности.
 *
 * «Не знаю» и ноль — разные ответы, и подменять первый вторым нельзя: ноль
 * означал бы, что человек не тратит повседневные нужды ничего, и весь остаток ушёл бы
 * в цели. Пустое поле — это «ещё не спрашивали», тоже не ноль.
 */
export function draftBaselines(d: Draft): { need: Amount; want: Amount } {
  const one = (raw: string, key: 'needBaseline' | 'wantBaseline'): Amount => {
    if (d.unknown.includes(key)) return unknownAmount('user_unknown');
    if (raw.trim() === '') return unknownAmount('not_asked');
    return confirmed(money(raw));
  };
  return { need: one(d.needBaseline, 'needBaseline'), want: one(d.wantBaseline, 'wantBaseline') };
}

/**
 * Платежи, срок которых в этом цикле уже прошёл.
 *
 * Цикл начинается с последнего прихода: платёж до него относится к прошлому
 * месяцу и нас не касается. Такие платежи человек почти наверняка уже оплатил
 * — остатки на счетах он называл после них, — и посчитать их предстоящими
 * значит вычесть одни и те же деньги дважды.
 */
/** Начало текущего цикла — последний приход; без дохода — неизвестно. */
function cycleStartOf(d: Draft, today: PlainDate): PlainDate | null {
  const starts = d.incomes
    .filter((inc) => parseAmountInput(inc.amount) > ZERO)
    .map((inc) => prevOccurrenceInclusive(today, incomeDay(inc), 'previous_business_day'));
  return starts.length === 0 ? null : starts.reduce((a, b) => (a >= b ? a : b));
}

/**
 * Срок платежа по кредиту уже прошёл в этом цикле: названная дата платежа
 * раньше сегодня. Тогда у строки кредита спрашивается «Уже оплачен».
 */
function loanPassed(d: Draft, l: DLoan, today: PlainDate): boolean {
  if (!paysMonthly(l) || parseAmountInput(l.payment) <= ZERO) return false;
  const due = loanPayDate(l, today);
  const start = cycleStartOf(d, today);
  return due < today && (start === null || due >= start);
}

function passedThisCycle(d: Draft, today: PlainDate): Array<{ index: number; due: PlainDate }> {
  const cycleStart = cycleStartOf(d, today);
  if (cycleStart === null) return [];

  // Срок берётся из расписания платежа, а не из числа месяца.
  //
  // Прежний перебор месячных чисел выдумывал наступления: недельной няне с
  // первым сроком 7 сентября и полем «10 числа» он приписывал оплату
  // 10 сентября — дня, которого в её календаре нет вовсе. Дальше эта
  // выдуманная оплата становилась настоящим расходом при сохранении.
  return d.obligations
    .map((o, index) => ({
      index,
      due: lastOccurrenceUpTo(draftSchedule(o, today), today),
    }))
    .filter((x): x is { index: number; due: PlainDate } => x.due !== null)
    .filter(({ index, due }) => due >= cycleStart && parseAmountInput(d.obligations[index]!.amount) > ZERO);
}

/**
 * Число прихода: первое из выбранных. Прежде шло отдельное `day` — 10-е, у
 * «Аванса» 25-е, — что бы человек ни выбрал, и по нему считались уже
 * прошедшие платежи и начало целей (22.10.2026).
 */
const incomeDay = (inc: DIncome): number =>
  ((inc.rhythm ?? 'dates') === 'dates' ? parseDays(inc.days)[0] : undefined) ?? dayNum(inc.day);

/**
 * День ближайшего прихода: с него цели и начинают копить, если человек не
 * назвал другую дату. Без прихода — с завтрашнего дня, чтобы цель не
 * забрала деньги из недожитого цикла.
 */
function firstIncome(d: Draft, today: PlainDate): PlainDate {
  return nextIncomeDate(
    d.incomes
      .filter((inc) => money(inc.amount) > ZERO)
      .map((inc, i) => ({
        id: `i${i}`, name: inc.name, accountId: 'a0', amount: money(inc.amount),
        isVariable: inc.variable, dayOfMonth: incomeDay(inc),
        shiftRule: 'previous_business_day' as const, isActive: true,
      })),
    today,
  ) ?? addDays(today, 1);
}

/** Одинаковый старт цели для предпросмотра и записи: неназванный — с прихода. */
export function draftGoalStart(start: string, d: Draft, today: PlainDate): PlainDate {
  return /^\d{4}-\d{2}-\d{2}$/.test(start) ? start as PlainDate : firstIncome(d, today);
}

/** Число месяца из строки: за пределами 1..31 смысла нет. */
const dayNum = (sv: string): number => Math.min(31, Math.max(1, Math.trunc(Number(sv) || 1)));
const money = (s: string): Money => parseAmountInput(s);

/** Что открыто в модальном листе. index === null — добавление новой записи. */
type Editing =
  | { list: 'loan'; index: number | null; init: DLoan }
  | { list: 'income'; index: number | null; init: DIncome }
  | { list: 'account'; index: number | null; init: DAccount }
  | { list: 'obligation'; index: number | null; init: DObligation }
  | { list: 'goal'; index: number | null; init: DGoal }
  | null;

export function Onboarding({ onDone, onSkip, onLogin }: {
  onDone: () => void;
  onSkip: () => void;
  /**
   * Вход для того, у кого аккаунт уже есть.
   *
   * Без него анкета — комната без двери: человек, поставивший приложение
   * заново или открывший его на другом устройстве, вынужден заполнять всё
   * второй раз, хотя его картина лежит в аккаунте.
   */
  onLogin: () => void;
}) {
  const [step, setStep] = useState<Step>('income');
  const [d, setD] = useState<Draft>(EMPTY_DRAFT);
  const [editing, setEditing] = useState<Editing>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);

  // Восстановление черновика. Раньше прерванная настройка пропадала целиком,
  // и человек возвращался к пустому первому вопросу — в самый неудачный
  // момент, когда он ещё ничего не получил взамен.
  useEffect(() => {
    void loadDraft().then((saved) => {
      if (!saved) return;
      setD({ ...EMPTY_DRAFT, ...(saved.data as Draft) });
      if (saved.step && saved.step !== 'income') { resuming.current = true; setStep(saved.step as Step); }
      setRestored(true);
      track('onb_restored');
    });
  }, []);

  // Отчёт владельцу: вход на шаг, сколько на нём пробыл, куда ушёл и сколько
  // записей оставил. Восстановление черновика — не переход, его не считаем.
  const stepMark = useRef<{ step: Step; at: number } | null>(null);
  const resuming = useRef(false);
  const itemsOn = (s: Step): number | undefined => (
    s === 'income' ? d.incomes.length
      : s === 'accounts' ? d.accounts.length
        : s === 'obligations' ? d.obligations.length + d.loans.length
          : s === 'goals' ? d.goals.length : undefined);
  useEffect(() => {
    const prev = stepMark.current;
    const now = Date.now();
    if (prev && prev.step !== step && !resuming.current) {
      const items = itemsOn(prev.step);
      track('onb_leave', {
        step: prev.step, ms: now - prev.at,
        to: STEP_ORDER.indexOf(step) > STEP_ORDER.indexOf(prev.step) ? 'next' : 'back',
        ...(items !== undefined ? { items } : {}),
      });
    }
    resuming.current = false;
    stepMark.current = { step, at: now };
    track('onb_step', { step });
  }, [step]);

  // Лист записи: сохранил или закрыл, и какие поля заполнил — без значений.
  const sheetEnd = useRef<{ saved?: object; removed?: boolean } | null>(null);
  const wasEditing = useRef<Editing>(null);
  useEffect(() => {
    const was = wasEditing.current;
    wasEditing.current = editing;
    if (!was || editing) return;
    const end = sheetEnd.current;
    sheetEnd.current = null;
    track('onb_sheet', {
      step, kind: was.list, isNew: was.index === null, saved: Boolean(end?.saved),
      ...(end?.removed ? { removed: true } : {}),
      ...(end?.saved ? { fields: filledFields(end.saved) } : {}),
    });
  }, [editing]);

  /*
    Скриншоты из банка (27.10.2026, docs/design/bank-shots-2026-10-26):
    счета и кредиты заводятся сами, человек проверяет. Пока читаются —
    миниатюры; потом — плашка над секцией, что нашлось и что проверить.
  */
  const [shotFiles, setShotFiles] = useState<{ files: File[]; for: 'accounts' | 'loans' } | null>(null);
  const [shotNote, setShotNote] = useState<{ title: string; text: string } | null>(null);
  const [shotErr, setShotErr] = useState<{ for: 'accounts' | 'loans'; text: string } | null>(null);
  const [loanNoteClosed, setLoanNoteClosed] = useState(false);
  /** Черновик на момент ответа сервера: чтение идёт секунды, за это время он не меняется. */
  const dRef = useRef(d);
  dRef.current = d;
  const asFound = (l: DLoan): FoundLoan => ({
    draft: l, scheduleKnown: l.shot?.scheduleKnown ?? true, bankId: l.shot?.bank ?? null, kind: (l.shot?.kind ?? 'consumer') as FoundLoan['kind'],
  });
  const readShots = async (files: File[], target: 'accounts' | 'loans', loanIndex: number | null = null) => {
    if (shotFiles) return;
    setShotErr(null); setShotFiles({ files, for: target });
    try {
      const found = collect(await readBankShots(files, 'onboarding'), todayPd());
      track('bank_shots', { step: target, accounts: found.accounts.length, loans: found.loans.length, files: files.length });
      const accounts = target === 'accounts' ? found.accounts : [];
      if (accounts.length === 0 && found.loans.length === 0) {
        setShotErr({ for: target, text: target === 'accounts'
          ? 'На скриншоте не нашлось ни счетов, ни кредитов. Сними главный экран банка, где видны карты и остатки, — или добавь вручную.'
          : 'На скриншоте не нашлось кредита. Открой кредит в приложении банка и сними экран с условиями.' });
        return;
      }
      const p = dRef.current;
      const nextAccounts = [...p.accounts];
      let added = 0;
      for (const a of accounts) {
        if (nextAccounts.some((x) => sameAccountName(x.name, a.draft.name))) continue;
        nextAccounts.push({ ...a.draft, ...(a.warn ? { shotWarn: a.warn } : {}) });
        added++;
      }
      const loans = [...p.loans];
      for (const l of found.loans) {
        // Экран условий дописывает тот кредит, ради которого его сняли;
        // иначе — найденный раньше тот же кредит; иначе — новый.
        const at = loanIndex !== null && loans[loanIndex] ? loanIndex
          : loans.findIndex((x) => x.shot && (x.shot.kind === l.kind || termsOnly(x) || termsOnly(l.draft))
            && (x.shot.bank === l.bankId || !x.shot.bank || !l.bankId));
        const merged = at >= 0 ? mergeLoan(asFound(loans[at]!), l) : l;
        const draft: DLoan = { ...merged.draft, shot: { bank: merged.bankId ?? l.bankId, scheduleKnown: merged.scheduleKnown, kind: merged.kind } };
        if (at >= 0) loans[at] = draft; else loans.push(draft);
      }
      const next = { ...p, accounts: nextAccounts, loans };
      setD(next);
      void saveDraft(step, next);
      setLoanNoteClosed(false);
      // Плашка «Нашлось…» — над списком; человек мог за это время уйти вниз к плиткам.
      window.scrollTo({ top: 0, behavior: 'smooth' });
      if (target === 'accounts') {
        const loanWord = found.loans[0] ? found.loans[0].draft.name : null;
        setShotNote({
          title: `Нашлось ${added} ${pluralWord(added, 'счёт', 'счёта', 'счетов')}`,
          text: [
            added > 0 ? 'Проверь жёлтое — нажми на строку, чтобы поправить.' : 'Эти счета уже в списке.',
            loanWord ? `${loanWord} будет на шаге с платежами.` : '',
            found.foreign > 0 ? 'Валютные счета пока не добавляются: SKOKA считает в рублях.' : '',
            found.dropped > 0 ? 'Часть экрана не прочиталась — проверь, все ли счета на месте.' : '',
          ].filter(Boolean).join(' '),
        });
      }
    } catch (e) {
      setShotErr({ for: target, text: e instanceof Error ? e.message : 'Не получилось прочитать скриншот' });
    } finally {
      setShotFiles(null);
    }
  };
  /** Кредит со скриншота, которому чего-то не хватает, — о нём плашка на шаге платежей. */
  const loanShot = (() => {
    const i = d.loans.findIndex((l) => l.shot);
    if (i < 0) return null;
    const l = d.loans[i]!;
    return { index: i, loan: l, missing: loanMissing({ ...l, scheduleKnown: l.shot!.scheduleKnown }) };
  })();

  // Платежи, срок которых в этом цикле уже прошёл: у их строк — «Уже оплачен».
  const passedNow = new Map(passedThisCycle(d, todayPd()).map((x) => [x.index, x.due]));
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((p) => {
    const next = { ...p, [k]: v };
    // Сохраняем после каждой правки: «продолжить позже» не должно требовать
    // отдельной кнопки, о которой человек в спешке не вспомнит.
    void saveDraft(step, next);
    return next;
  });

  /** Переход к следующему блоку: шаг тоже часть черновика. */
  const go = (next: Step, nextDraft: Draft = d): void => {
    if (nextDraft !== d) setD(nextDraft);
    setStep(next);
    void saveDraft(next, nextDraft);
  };

  /** Запись из листа: новая встаёт в конец, существующая заменяется на месте. */
  const put = async <T,>(list: keyof Draft, index: number | null, value: T) => {
    const arr = [...(d[list] as unknown as T[])];
    if (index === null) arr.push(value);
    else arr[index] = value;
    const next = { ...d, [list]: arr };
    // Лист закрывается только после записи: перезагрузка уже видимого
    // результата должна восстановить именно его, без перехода к следующему шагу.
    await saveDraft(step, next);
    sheetEnd.current = { saved: value as object };
    setD(next);
    setEditing(null);
  };
  const drop = async (list: keyof Draft, index: number | null) => {
    if (index !== null) {
      let next = { ...d, [list]: (d[list] as unknown as unknown[]).filter((_, i) => i !== index) };
      if (list === 'accounts') {
        // Удалённую ссылку снимаем, следующие сдвигаем вместе с черновиком.
        const shift = (ref: string | undefined): string | undefined => {
          const n = ref && /^a\d+$/.test(ref) ? Number(ref.slice(1)) : null;
          if (n === null) return ref;
          return n === index ? undefined : n > index ? `a${n - 1}` : ref;
        };
        next = {
          ...next,
          obligations: next.obligations.map(({ accountId, ...o }) => ({ ...o, ...(shift(accountId) ? { accountId: shift(accountId)! } : {}) })),
          goals: next.goals.map(({ accountId, ...g }) => ({ ...g, ...(shift(accountId) ? { accountId: shift(accountId)! } : {}) })),
          loans: next.loans.map(({ accountId, ...l }) => ({ ...l, ...(shift(accountId) ? { accountId: shift(accountId)! } : {}) })),
        };
      }
      await saveDraft(step, next);
      setD(next);
    }
    sheetEnd.current = { removed: true };
    setEditing(null);
  };

  // Счета и операции создаются только на последнем шаге (PRD §4.3).
  // До него сохраняется отдельный черновик для продолжения анкеты.
  const commit = async () => {
    setBusy(true); setErr(null);
    try {
      const created: Account[] = [];
      /** Счёт черновика (`a0`, `a3`) → созданный: на него ссылаются платежи и цели. */
      const accountIds = new Map<string, string>();
      // Черновая ссылка цели на долг («card:0», «loan:1») меняется на
      // настоящий идентификатор кредита: до сохранения его ещё не существует.
      const debtIds = new Map<string, string>();
      for (const [ai, a] of d.accounts.entries()) {
        if (!a.name.trim()) continue;
        // Тем же путём, что «+» на «Счетах»: кредитка — счёт, условия и долг.
        const { account, loan } = await createAccount(a);
        if (loan) debtIds.set(`card:${ai}`, loan.id);
        accountIds.set(`a${ai}`, account.id);
        created.push(account);
      }
      if (created.length === 0) {
        created.push(await addAccount({ name: 'Основная карта', kind: 'debit', balance: ZERO }));
      }
      // Доход и обязательства привязываются к первому тратимому счёту: с
      // накопительного зарплата не приходит и платежи не уходят.
      const acc = created.find((a) => a.isLiquid) ?? created[0]!;

      for (const inc of d.incomes) {
        // «Пока не жду» — тоже ответ, и его надо сохранить: иначе человек
        // каждый раз будет видеть вопрос, на который уже ответил.
        if (money(inc.amount) === ZERO && inc.rhythm !== 'irregular' && inc.rhythm !== 'none') continue;
        const stream = await addIncome({
          name: inc.name.trim() || 'Приход', accountId: acc.id,
          amount: money(inc.amount), dayOfMonth: incomeDay(inc), isVariable: inc.variable,
          rhythm: draftRhythm(inc),
          amountBasis: money(inc.amount) === ZERO ? 'unknown' : inc.variable ? 'estimate' : 'known',
          horizonRole: inc.isCheckpoint ? 'checkpoint' : 'top_up',
          ...(inc.advance ? { advance: true } : {}),
        });
        // Конкретный ожидаемый гонорар — отдельная запись, а не расписание:
        // он случается один раз, и обучать им ритм нельзя.
        if (inc.rhythm === 'irregular' && isDate(inc.expectDate)) {
          await addExpectedIncome({
            name: inc.name.trim() || 'Приход',
            amount: money(inc.expectAmount),
            expectedDate: inc.expectDate as PlainDate,
            accountId: acc.id,
            streamId: stream.id,
            ...(inc.expectIsCheckpoint ? { horizonRole: 'checkpoint' as const } : {}),
          });
        }
      }
      // Платежи, срок которых уже прошёл и которые человек оставил отмеченными,
      // сразу помечаются оплаченными. Расход попадает в историю, но баланс не
      // трогается: деньги ушли до того, как человек назвал остаток. Иначе одни
      // и те же деньги вычитаются дважды.
      const passed = new Map(passedThisCycle(d, todayPd()).map((x) => [x.index, x.due]));
      for (const [i, o] of d.obligations.entries()) {
        if (money(o.amount) === ZERO || !o.name.trim()) continue;
        const schedule = draftSchedule(o, todayPd());
        const payFrom = (o.accountId && accountIds.get(o.accountId)) || acc.id;
        const created = await addObligation({
          name: o.name.trim(), accountId: payFrom, amount: money(o.amount),
          // Ритм и окончание сохраняются вместе с платежом: иначе годовая
          // страховка, названная годовой на входе, после сохранения снова
          // становится ежемесячной, а разовый налог — двенадцатью налогами.
          dayOfMonth: Number(schedule.anchor.slice(8, 10)),
          isVariable: o.variable,
          schedule,
        });
        const due = passed.get(i);
        if (due && !d.stillUnpaid.includes(i)) {
          await settleObligation({
            obligationId: created.id,
            dueDate: due,
            amount: money(o.amount),
            paidDate: due,
            accountId: payFrom,
            applyToBalance: false,
          });
        } else if (due) {
          /*
            Человек снял галочку: платёж наступил и **не** оплачен.

            Это его явное решение, а не пустота. Без записи оно терялось:
            наступление оказывалось раньше начала учёта, и приложение
            переставало считать деньги связанными — а потом, при записи
            оплаты, «Повседневные нужды» падало на всю сумму разом.
          */
          await decideInstance({
            obligationId: created.id,
            scheduledDate: due,
            decision: 'unpaid',
            requiredAt: money(o.amount),
          });
        }
      }
      // Кредит владеет своим платежом: обязательство создаётся один раз и
      // привязывается к нему. Записать платёж отдельно значило бы вычесть его
      // из доступного дважды.
      for (const [li, l] of d.loans.entries()) {
        if (!l.name.trim() || (paysMonthly(l) && money(l.payment) === ZERO)) continue;
        const loan = await saveDraftLoan(l, (l.accountId && accountIds.get(l.accountId)) || acc.id);
        debtIds.set(`loan:${li}`, loan.id);
        // Сняли «Уже оплачен» у кредита — платёж наступил и не оплачен: деньги на него остаются отложенными.
        if (loan.obligationId && loanPassed(d, l, todayPd()) && (d.loansUnpaid ?? []).includes(li)) {
          await decideInstance({
            obligationId: loan.obligationId, scheduledDate: loanPayDate(l, todayPd()),
            decision: 'unpaid', requiredAt: money(l.payment),
          });
        }
      }

      // «Не знаю» сохраняется статусом, а не нулём: иначе человек, честно
      // сказавший «пока не знаю», получил бы бесконечный запас хода — ровно
      // тот ответ, который выглядит хорошо и неверен.
      //
      // Пустое поле — тоже «не знаю», как и на самом шаге (`draftBaselines`).
      // Прежде здесь оно сохранялось подтверждённым нулём, и после
      // «Пока не знаю» главная обещала «хватит на 90 дней» и «запас на месяц
      // есть» при пустых счетах (15.10.2026).
      const baselines = draftBaselines(d);
      await setBaselineAmounts(baselines.need, baselines.want);

      // День ближайшего прихода: с него цели и начинают копить, если человек
      // не назвал другую дату. Без прихода — с завтрашнего дня, чтобы цель не
      // забрала деньги из недожитого цикла.

      for (const g of d.goals) {
        if (money(g.target) === ZERO || !g.name.trim()) continue;
        const holdOn = g.accountId ? created.find((a) => a.id === accountIds.get(g.accountId!)) : undefined;
        const goal = await addGoal({
          name: g.name.trim(), kind: g.kind,
          ...(holdOn ? { accountId: holdOn.id } : {}),
          priority: g.priority,
          targetAmount: money(g.target), currentAmount: money(g.current),
          monthlyContribution: money(g.monthly),
          ...(g.debtRef && debtIds.has(g.debtRef) ? { loanId: debtIds.get(g.debtRef)! } : {}),
          ...(/^\d{4}-\d{2}-\d{2}$/.test(g.date) ? { targetDate: g.date as PlainDate } : {}),
          // Без явной даты начала копим с ближайшего прихода: цель — это
          // распределение будущего дохода, а не остатка недожитого цикла.
          startDate: draftGoalStart(g.start, d, todayPd()),
        });
        // Накопленное на карте для трат закрепляется под цель — как в
        // приложении: иначе те же деньги посчитаются и в «Можно тратить».
        if (holdOn?.isLiquid && money(g.current) > ZERO) {
          await setReserve({ accountId: holdOn.id, forKind: 'goal', forId: goal.id, amount: money(g.current) });
        }
      }
      // Настройка завершена: черновик больше не нужен, а его наличие увело бы
      // человека обратно в анкету при следующем запуске.
      // С этого дня приложение отвечает за картину: раньше него автоматически
      // найденную просрочку вычитать нельзя.
      // Выбор режима — решение человека, а не умолчание приложения.
      if (d.planMode) await setPlanMode(d.planMode);
      await setMeta('obligationsKnown', d.obligationsKnown === true);
      await markTrackingStarted();
      await clearDraft();
      if (stepMark.current) track('onb_leave', { step: stepMark.current.step, ms: Date.now() - stepMark.current.at, to: 'next' });
      track('onb_done');
      onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Не удалось сохранить');
      setBusy(false);
    }
  };

  const progress = ['income', 'accounts', 'obligations', 'baseline', 'goals', 'verdict'].indexOf(
    step === 'firstNumber' ? 'accounts' : step,
  );

  const hasBuffer = d.goals.some((g) => g.kind === 'emergency_fund');
  const monthlyNeed = add(
    money(d.needBaseline),
    d.obligations.reduce((a, o) => add(a, money(o.amount)), ZERO),
  );
  const bufferTarget = share(monthlyNeed, 3);

  return (
    <div className="phone">
      <div className="bgart" />
      <main className="onbmain">
        <header className="onbhead">
          <Wordmark />
          <button className="skip" onClick={onSkip}>Пропустить</button>
        </header>
        {restored && (
          <div className="note">
            Продолжаем с того места, где остановились. Всё введённое сохранено.
          </div>
        )}
        <div className="steps">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <i key={i} className={i <= progress ? 'on' : ''} />
          ))}
        </div>

        {step === 'income' && (
          <Block
            title="Сколько и когда приходит?"
            hint={<>
              Начинаем с дохода: без него неизвестно, сколько дней до зарплаты — а от этого зависит вся главная цифра.
              {d.incomes.some((i) => i.variable) && (
                <>
                  {' '}У плавающего прихода в планировании беру нижнюю границу — 90% от названной.
                  Оптимистичная оценка дохода означает сломанное обещание в конце месяца.
                </>
              )}
            </>}
            onNext={() => go('accounts')}
            // Блокировки больше нет: у человека может не быть дохода вовсе
            // либо он может не знать сумму. Оба состояния — допустимые
            // ответы, и отсекать их на входе значит не пускать в продукт
            // ровно тех, ради кого он расширялся.
            nextEnabled
            nextLabel={d.incomes.some((i) => money(i.amount) > ZERO) ? 'Дальше' : 'Пока без дохода'}
          >
            <Compact
              rows={d.incomes.map((inc, i) => ({
                key: String(i),
                title: inc.name || 'Приход',
                detail: [
                  formatMoney(money(inc.amount)),
                  // Ритм словами, а не «N числа»: человеку, ответившему
                  // «когда как», приложение называло выдуманное число
                  // месяца — и он читал это как непонятую его жизнь.
                  rhythmText(draftRhythm(inc)),
                  ...(inc.variable ? ['сумма плавает'] : []),
                ].join(' · '),
                onEdit: () => setEditing({ list: 'income', index: i, init: inc }),
              }))}
              chips={[
                ...(d.incomes.some((x) => x.name === 'Зарплата') ? [] : [{
                  label: 'Зарплата',
                  onAdd: () => setEditing({ list: 'income', index: null, init: newIncome('Зарплата', '10') }),
                }]),
                ...(d.incomes.some((x) => x.name === 'Аванс') ? [] : [{
                  label: 'Аванс',
                  onAdd: () => setEditing({ list: 'income', index: null, init: { ...newIncome('Аванс', '25'), advance: true } }),
                }]),
                { label: 'Другое', onAdd: () => setEditing({ list: 'income', index: null, init: newIncome() }) },
              ]}
              empty="Добавь зарплату или другой приход — с него всё считается."
            />
          </Block>
        )}

        {step === 'accounts' && (
          <Block
            title="Где лежат деньги?"
            hint="Карта, наличные, накопительный — всё, из чего тратишь или копишь. Кредитка — тоже сюда: её долг учтём, а лимит деньгами считать не будем."
            onBack={() => setStep('income')}
            onNext={() => go('firstNumber')}
            // Ноль на счёте — это картина человека, а не отсутствие данных.
            // Пока читаются скриншоты, дальше не уходим: найденное легло бы в шаг, которого уже не видно.
            nextEnabled={!shotFiles}
            nextLabel={shotFiles ? 'Читаю…' : d.accounts.length === 0 ? 'Пока без счетов' : 'Дальше'}
            bare
            top={shotNote && (
              <ShotPlate title={shotNote.title} text={shotNote.text} onClose={() => setShotNote(null)} />
            )}
          >
            {/*
              Добавить — четырьмя плитками со значками, а не пунктирными
              метками: метки не походили на кнопки, и «на счетах» не
              подсказывало, что наличные — тоже сюда (22.10.2026). Вид
              меняется и в самом листе — «Другое» больше не нужно.
            */}
            <Compact
              rows={d.accounts.map((a, i) => ({
                key: String(i),
                title: a.name || 'Счёт',
                // «не трачу» рядом с «тратишь отсюда или копишь?» спорило бы само с собой.
                detail: a.shotWarn ? `${accountDetail({ ...a, liquid: true })} · ${a.shotWarn}` : accountDetail(a),
                warn: Boolean(a.shotWarn),
                // Значок — у каждого счёта: логотип банка или вид, как в «Деньгах».
                icon: <AccountGlyph account={{ kind: a.kind, ...(a.icon ? { icon: a.icon } : {}), ...(a.color ? { color: a.color } : {}) }} />,
                onEdit: () => setEditing({ list: 'account', index: i, init: a }),
              }))}
              lead={<>
                {shotFiles?.for === 'accounts' ? <ShotReading files={shotFiles.files} hint="Счета появятся в списке сами — обычно до 10 секунд" /> : (
                  <ShotTile title={d.accounts.length > 0 ? 'Ещё скриншот' : 'Скриншоты из банка'}
                    sub={d.accounts.length > 0 ? 'другой банк или счёт, которого нет в списке'
                      : 'главный экран банка, где видны счета и остатки — они добавятся сами'}
                    onFiles={(f) => void readShots(f, 'accounts')} />
                )}
                {!shotFiles && <div className="note shotnote">Скриншот не хранится: берутся только названия и суммы.</div>}
                {shotErr?.for === 'accounts' && <div className="note warn">{shotErr.text}</div>}
              </>}
              addLabel={shotFiles ? 'Пока читается — можно добавить вручную' : 'Или добавь вручную'}
              tiles={([
                ['debit', 'credit-card', 'Карта', 'дебетовая'],
                ['cash', 'wallet', 'Наличные', 'в кошельке'],
                ['savings', 'piggy-bank', 'Накопительный', 'вклад, копилка'],
                ['credit_card', 'credit-card-front', 'Кредитка', 'долг и лимит'],
              ] as const).map(([kind, icon, label, sub]) => ({
                icon, label, sub,
                onAdd: () => setEditing({ list: 'account', index: null, init: newAccount(onbAccountName(kind, d.accounts), kind) }),
              }))}
              chips={[]}
              empty="Начни с той карты, откуда чаще всего тратишь."
              split
            />
          </Block>
        )}

        {step === 'firstNumber' && <FirstNumber d={d} onNext={() => go('obligations')} onAccounts={() => setStep('accounts')} />}

        {step === 'obligations' && (
          <Block
            title="Какие обязательные платежи?"
            hint={<>
              Это 60–80% расходов. Без них цифра сверху завышена — она не знает про ипотеку и садик.
              {' '}Подсказки — не перечень: «Другое» добавит любой платёж. Люди забывают платежи,
              а забытый платёж означает завышенную цифру и потерянное доверие на первой неделе.
            </>}
            onBack={() => setStep('accounts')}
            onNext={() => go('baseline', { ...d, obligationsKnown: true })}
            nextEnabled={!shotFiles}
            nextLabel={shotFiles ? 'Читаю…' : d.obligations.length === 0 && d.loans.length === 0 ? 'У меня их нет' : 'Дальше'}
            bare
            top={loanShot && !loanNoteClosed && (
              <ShotPlate title={`Со скриншотов: ${loanShot.loan.name}`}
                text={loanShot.missing.length === 0
                  ? 'Остаток, платёж, ставка и сроки — оттуда. Проверь — нажми на строку.'
                  : loanShot.missing.length === 1 && loanShot.missing[0] === 'графика'
                    ? 'Остаток, платёж, ставка и сроки — оттуда. График — равный или убывающий — на скриншотах не виден: нажми на строку и проверь.'
                    : `Не хватает ${listRu(loanShot.missing)}. Открой кредит в приложении банка и сними экран с условиями.`}
                {...(loanShot.missing.some((m) => m !== 'графика') ? {
                  action: 'Добавить скриншот условий', busy: shotFiles?.for === 'loans',
                  onFiles: (f: File[]) => void readShots(f, 'loans', loanShot.index),
                } : {})}
                onClose={() => setLoanNoteClosed(true)} />
            )}
          >
            <Compact
              rows={[
                // Кредиты стоят вместе с платежами: для человека это один и тот
                // же ежемесячный отток, просто у кредита есть ещё остаток и ставка.
                ...d.loans.map((l, i) => {
                  const miss = l.shot ? loanMissing({ ...l, scheduleKnown: true }) : [];
                  // Ставка не прочиталась — это не «без процентов»: строка говорит, что есть и чего нет.
                  const known = [
                    l.principal ? `остаток ${formatMoney(money(l.principal))}` : '',
                    l.payment ? `${formatMoney(money(l.payment))}${l.payDate ? ` до ${Number(l.payDate.slice(8, 10))} числа` : ''}` : '',
                  ].filter(Boolean).join(' · ');
                  return {
                    key: `l${i}`,
                    title: l.name || (l.kind === 'private' ? 'Личный долг' : 'Кредит'),
                    detail: miss.length > 0 ? `${known} · нет ${listRu(miss.slice(0, 2))}` : loanDraftDetail(l),
                    warn: miss.length > 0,
                    ...(l.shot?.bank ? { icon: <IconGlyph icon={`bank:${l.shot.bank}`} color="violet" /> } : {}),
                    onEdit: () => setEditing({ list: 'loan', index: i, init: l }),
                    ...(loanPassed(d, l, todayPd()) ? { paid: {
                      label: `Уже оплачен ${formatDateDay(loanPayDate(l, todayPd()))}`,
                      on: !(d.loansUnpaid ?? []).includes(i),
                      toggle: (on: boolean) => set('loansUnpaid', on
                        ? (d.loansUnpaid ?? []).filter((x) => x !== i)
                        : [...(d.loansUnpaid ?? []), i]),
                    } } : {}),
                  };
                }),
                ...d.obligations.map((o, i) => ({
                  key: String(i),
                  title: o.name || 'Платёж',
                  detail: [
                    formatMoney(money(o.amount)),
                    // Ритм читается прямо из расписания: списку и расчёту
                    // положено говорить одно и то же.
                    scheduleLabel(draftSchedule(o, todayPd())),
                    ...(o.variable ? ['сумма плавает'] : []),
                  ].join(' · '),
                  onEdit: () => setEditing({ list: 'obligation', index: i, init: o }),
                  /*
                    Срок в этом цикле уже прошёл — «оплачен?» спрашивается здесь, у
                    самого платежа (03.11.2026), а не отдельным блоком на финале:
                    там человек уже не помнил, что вводил.
                  */
                  ...(passedNow.has(i) ? { paid: {
                    label: `Уже оплачен ${formatDateDay(passedNow.get(i)!)}`,
                    on: !d.stillUnpaid.includes(i),
                    toggle: (on: boolean) => set('stillUnpaid', on
                      ? d.stillUnpaid.filter((x) => x !== i)
                      : [...d.stillUnpaid, i]),
                  } } : {}),
                })),
              ]}
              chips={[
                { label: 'Ипотека или кредит',
                  onAdd: () => setEditing({ list: 'loan', index: null, init: newLoan('Ипотека', 'amortizing', '12') }) },
                { label: 'Рассрочка',
                  onAdd: () => setEditing({ list: 'loan', index: null, init: newLoan('Рассрочка', 'installment', '0') }) },
                { label: 'Личный долг',
                  onAdd: () => setEditing({ list: 'loan', index: null, init: newLoan('', 'private', '0') }) },
                ...PRESETS.filter((p) => !d.obligations.some((o) => o.name === p)).map((p) => ({
                  label: p,
                  onAdd: () => setEditing({
                    list: 'obligation', index: null, init: newObligation(p, VARIABLE_PRESETS.has(p)),
                  }),
                })),
                { label: 'Другое', onAdd: () => setEditing({ list: 'obligation', index: null, init: newObligation('') }) },
              ]}
              empty="Ипотека, аренда, садик, подписки — всё, что уходит само каждый месяц."
              split
              lead={<>
                {shotFiles?.for === 'loans' ? <ShotReading files={shotFiles.files} hint="Кредит появится в списке сам — обычно до 10 секунд" /> : (
                  <ShotTile title="Скриншот кредита из банка" sub="экран условий: остаток, платёж, ставка, срок"
                    onFiles={(f) => void readShots(f, 'loans')} />
                )}
                {shotErr?.for === 'loans' && <div className="note warn">{shotErr.text}</div>}
              </>}
              addLabel={shotFiles ? 'Пока читается — можно добавить вручную' : 'Или добавь вручную'}
            />
          </Block>
        )}

        {step === 'baseline' && (
          <Block
            title="Сколько уходит на повседневные нужды?"
            hint="Помимо платежей выше: продукты, транспорт, бытовое. Оценка грубая — без неё нечем считать, сколько можно тратить."
            onBack={() => setStep('obligations')}
            onNext={() => go('goals')}
            nextEnabled
            nextLabel={money(d.needBaseline) > ZERO || d.unknown.includes('needBaseline')
              ? 'Дальше' : 'Пока не знаю'}
          >
            <label>Повседневные нужды в месяц
              {!d.unknown.includes('needBaseline') && (
                <Tip>Уточнится само, когда наберётся история трат. Сейчас важен порядок
                  величины, а не точность до рубля.</Tip>
              )}
              <AmountInput value={d.needBaseline} onChange={(x) => {
                set('needBaseline', x);
                // Названная сумма отменяет ответ «не знаю»: человек ответил.
                if (d.unknown.includes('needBaseline')) {
                  set('unknown', d.unknown.filter((u) => u !== 'needBaseline'));
                }
              }} placeholder="30 000" /></label>
            <label>Свободные траты в месяц
              <AmountInput value={d.wantBaseline} onChange={(x) => set('wantBaseline', x)}
                placeholder="5 000" /></label>

            {/* «Не знаю» — ответ, а не пропуск: расчёт после него честно
                скажет, чего не хватает, вместо выдуманного запаса в днях. */}
            <label className="check">
              <input type="checkbox" checked={d.unknown.includes('needBaseline')}
                onChange={(e) => set('unknown', e.target.checked
                  ? [...d.unknown.filter((u) => u !== 'needBaseline'), 'needBaseline' as const]
                  : d.unknown.filter((u) => u !== 'needBaseline'))} />
              Пока не знаю, сколько уходит
              {d.unknown.includes('needBaseline') && (
                <Tip>Тогда часть ответов останется приблизительной: я не смогу сказать,
                  на сколько дней хватит денег, и не стану это выдумывать.</Tip>
              )}
            </label>
          </Block>
        )}

        {step === 'goals' && (
          <Block
            title="На что копишь?"
            hint={<>
              Цели конкурируют за одни деньги, поэтому приложение считает, что успеет, а что придётся сдвинуть.
              {!hasBuffer && monthlyNeed > ZERO && (
                <>
                  {' '}Подушка посчитана как три месяца твоих обязательных расходов: если доход
                  пропадёт, есть три месяца на решение.
                </>
              )}
            </>}
            onBack={() => setStep('baseline')}
            onNext={() => go('verdict')}
            nextEnabled
            nextLabel={d.goals.length === 0 ? 'Пока без целей' : 'Посмотреть итог'}
          >
            <Compact
              rows={d.goals.map((g, i) => ({
                key: String(i),
                title: g.name || 'Цель',
                detail: goalDetail(g),
                onEdit: () => setEditing({ list: 'goal', index: i, init: g }),
              }))}
              chips={[
                ...(!hasBuffer && monthlyNeed > ZERO ? [{
                  label: `Подушка ${formatMoney(bufferTarget)}`,
                  primary: true,
                  onAdd: () => setEditing({
                    list: 'goal', index: null,
                    init: newGoal(
                      'Подушка', 'emergency_fund',
                      formatAmountInput(String(bufferTarget / 100)), 'high',
                    ),
                  }),
                }] : []),
                // «Закрыть долг» целью не заводится: досрочное — в самом долге,
                // калькулятором в «Долгах». Цель копила бы деньги, которые на
                // деле уходят банку.
                { label: 'Своя цель', onAdd: () => setEditing({ list: 'goal', index: null, init: newGoal('', 'purchase') }) },
              ]}
              empty="Подушка, крупная покупка — что угодно с суммой и сроком."
            />
          </Block>
        )}

        {step === 'verdict' && (
          <Verdict d={d} set={set} busy={busy} err={err}
            onBack={() => setStep('goals')} onCommit={commit}
            onFixBaseline={() => setStep('baseline')} onStep={(x) => setStep(x)} />
        )}

        {/* Внизу, а не в шапке: рядом с «Пропустить» это был бы второй выход
            из двух слов, и человек выбирал бы между ними вместо того, чтобы
            заполнять анкету. Внизу он находит вход тогда, когда вспомнил про
            аккаунт, — и не раньше. */}
        <div className="welcome-foot onbfoot">
          <span>Есть аккаунт? <button className="lnk" onClick={onLogin}>Войти</button></span>
        </div>
      </main>

      {editing?.list === 'loan' && (
        <DebtSheet init={editing.init} isNew={editing.index === null} today={todayPd()}
          // Счета введены шагом раньше: платёж по долгу — с того, что выбран.
          accounts={payAccounts(draftEntities(d, todayPd()).accounts)}
          accountId={draftEntities(d, todayPd()).accounts.find((a) => a.id === editing.init.accountId)?.id
            ?? draftEntities(d, todayPd()).spendable}
          {...(editing.init.shot ? (() => {
            const miss = loanMissing({ ...editing.init, scheduleKnown: editing.init.shot.scheduleKnown });
            return {
              title: 'Проверь кредит',
              lead: <ShotPlate title={`Со скриншотов${bankName(editing.init.shot.bank) ? ` · ${bankName(editing.init.shot.bank)}` : ''}`}
                text={miss.length === 0 ? 'Всё, что нужно для расчёта, — оттуда. Проверь и сохрани.'
                  : miss.length === 1 && miss[0] === 'графика'
                    ? 'Остальное — оттуда. График — равный или убывающий — на скриншотах не виден: проверь в «Ещё условия».'
                    : `Не хватает ${listRu(miss)}. Впиши руками или сними экран с условиями кредита в приложении банка.`}
                {...(miss.some((m) => m !== 'графика') ? {
                  action: 'Добавить скриншот условий',
                  onFiles: (f: File[]) => { const i = editing.index; setEditing(null); void readShots(f, 'loans', i); },
                } : {})} />,
            };
          })() : {})}
          onClose={() => setEditing(null)}
          onSave={(v, extra) => put('loans', editing.index, { ...v, shot: undefined, ...(extra.accountId ? { accountId: extra.accountId } : {}) })}
          onDelete={() => { if (confirm('Удалить долг?')) drop('loans', editing.index); }} />
      )}
      {editing?.list === 'income' && (
        <IncomeSheet init={editing.init} isNew={editing.index === null}
          onClose={() => setEditing(null)}
          onSave={(v) => put('incomes', editing.index, v)}
          onDelete={() => drop('incomes', editing.index)} />
      )}
      {editing?.list === 'account' && (
        <AccountSheet init={editing.init} isNew={editing.index === null}
          defaultName={(k) => onbAccountName(k, d.accounts)}
          {...(editing.init.shotWarn ? {
            title: 'Проверь счёт',
            lead: <ShotPlate title={`Со скриншота${bankName(editing.init.icon?.replace('bank:', '') ?? null) ? ` · ${bankName(editing.init.icon!.replace('bank:', ''))}` : ''}`}
              text={editing.init.kind === 'credit_card'
                ? 'Долг, лимит и платёж — оттуда. Проверь, до какого числа платить и беспроцентный период.'
                : 'Название, сумма и значок — оттуда. Вид счёта и «Тратим отсюда» на скриншоте не видно — проверь.'} />,
          } : {})}
          onClose={() => setEditing(null)}
          // Человек посмотрел и сохранил — жёлтая подпись больше не нужна.
          onSave={(v) => put('accounts', editing.index, { ...v, shotWarn: undefined })}
          onDelete={() => drop('accounts', editing.index)} />
      )}
      {editing?.list === 'obligation' && (
        <ObligationSheet isNew={editing.index === null}
          init={{
            name: editing.init.name, amount: editing.init.amount, variable: editing.init.variable,
            // Черновик, заведённый до появления полей ритма, открывается
            // ежемесячным по своему числу — ровно так он и считался.
            sched: draftRepeat(editing.init, todayPd()),
            accountId: draftEntities(d, todayPd()).accounts.find((a) => a.id === editing.init.accountId)?.id
              ?? draftEntities(d, todayPd()).spendable,
          }}
          accounts={payAccounts(draftEntities(d, todayPd()).accounts)}
          onClose={() => setEditing(null)}
          onSave={(v) => put('obligations', editing.index, {
            name: v.name, amount: v.amount, variable: v.variable, sched: v.sched,
            day: String(Number(v.sched.date.slice(8, 10))),
            ...(v.accountId ? { accountId: v.accountId } : {}),
          })}
          onDelete={() => drop('obligations', editing.index)} />
      )}
      {editing?.list === 'goal' && (
        <GoalSheet isNew={editing.index === null}
          init={{
            ...editing.init,
            // Новая цель копится на накопительном, если он есть, — как в приложении.
            ...(editing.index === null && !editing.init.accountId
              ? { accountId: defaultGoalAccount(savingAccounts(draftEntities(d, todayPd()).accounts)) || undefined } : {}),
          } as GoalDraft}
          accounts={savingAccounts(draftEntities(d, todayPd()).accounts)}
          hasBuffer={hasBuffer}
          {...(monthlyNeed > ZERO ? {
            buffer: {
              target: formatAmountInput(String(bufferTarget / 100)),
              text: `3 месяца обязательных трат: ${formatMoney(monthlyNeed)} × 3`,
            },
          } : {})}
          startText="с ближайшей зарплаты" startSub={formatDateShort(firstIncome(d, todayPd()))}
          onClose={() => setEditing(null)}
          onSave={(v) => put('goals', editing.index, v)}
          onDelete={() => drop('goals', editing.index)} />
      )}
    </div>
  );
}

/** Подпись под названием счёта: то, что человек хочет проверить одним взглядом. */
function accountDetail(a: DAccount): string {
  if (a.kind === 'credit_card') {
    // Лимит в компактную строку не идёт: это не деньги человека, а место в
    // ней ограничено. Долг и срок платежа — то, что проверяют взглядом.
    return `долг ${formatMoney(money(a.balance))} · до ${dayNum(a.payDay)} числа`;
  }
  const parts = [formatMoney(money(a.balance))];
  if (!liquidOf(a)) parts.push('не трачу');
  if (a.kind === 'cash') parts.push('наличные');
  return parts.join(' · ');
}



function goalDetail(g: DGoal): string {
  const parts = [formatMoney(money(g.target))];
  if (/^\d{4}-\d{2}-\d{2}$/.test(g.start)) parts.push(`с ${formatDateShort(g.start)}`);
  if (money(g.current) > ZERO) parts.push(`накоплено ${formatMoney(money(g.current))}`);
  if (money(g.monthly) > ZERO) parts.push(`${formatMoney(money(g.monthly))} в месяц`);
  if (/^\d{4}-\d{2}-\d{2}$/.test(g.date)) parts.push(`до ${formatDateShort(g.date)}`);
  return parts.join(' · ');
}

/**
 * Компактный список плюс чипсы добавления.
 *
 * Раскрытые формы прямо на экране не складываются: пять записей — и шаг
 * превращается в прокрутку, где не видно ни списка целиком, ни кнопки
 * «Дальше». Здесь запись занимает две строки, а вся сложность ввода живёт
 * в модальном листе.
 */
function Compact({ rows, chips, tiles, empty, lead, addLabel, split = false }: {
  rows: Array<{ key: string; title: string; detail: string; onEdit: () => void; icon?: React.ReactNode; warn?: boolean;
    /** Срок уже прошёл: «Уже оплачен 15 октября» — переключатель под строкой (03.11.2026). */
    paid?: { label: string; on: boolean; toggle: (on: boolean) => void } }>;
  chips: Array<{ label: string; onAdd: () => void; primary?: boolean }>;
  /** Добавить — плитками со значком и подписью, а не метками. */
  tiles?: Array<{ icon: string; label: string; sub: string; onAdd: () => void }>;
  empty: string;
  /** Перед ручным добавлением — «Скриншоты из банка». */
  lead?: React.ReactNode;
  /** Подпись над ручным добавлением, когда выше есть другой путь. */
  addLabel?: string;
  /**
   * Два пути — отдельно (27.10.2026): список добавленного — в белой секции,
   * «скриншот или вручную» — под ней, на фоне страницы. Плитки на белом
   * читались плохо. Шагу с `split` секцию даёт сам список (`Block bare`).
   */
  split?: boolean;
}) {
  const list = rows.length === 0 ? null : (
    <div className="clist">
      {rows.map((r) => (
        // Нажатие по всей строке, а не по иконке: цель размером в строку
        // не требует прицеливания, а карандаш говорит, что строка правится.
        <Fragment key={r.key}>
        <button className="crow" onClick={r.onEdit}>
          {r.icon}
          <div className="cmain">
            <div className="cttl">{r.title}</div>
            <div className={`cdet${r.warn ? ' warn' : ''}`}>{r.detail}</div>
          </div>
          <span className="cedit" aria-hidden="true"><PencilIcon /></span>
        </button>
        {r.paid && (
          <label className="li sw cpaid">
            <div className="nm"><b>{r.paid.label}</b>
              <span>{r.paid.on ? 'остатки названы уже после него' : 'деньги на него останутся отложенными'}</span></div>
            <input type="checkbox" checked={r.paid.on} onChange={(e) => r.paid!.toggle(e.target.checked)} />
            <span className="swbox" aria-hidden="true" />
          </label>
        )}
        </Fragment>
      ))}
    </div>
  );
  const add = <>
    {lead}
    {addLabel && <div className="note lbl">{addLabel}</div>}
    {tiles && (
      <div className="addtiles">
        {tiles.map((t) => (
          <button key={t.label} type="button" className="addtile" onClick={t.onAdd} aria-label={`Добавить: ${t.label}`}>
            <span className="addtile-i"><Icon name={t.icon} size={20} /></span>
            <b>{t.label}</b><span>{t.sub}</span>
          </button>
        ))}
      </div>
    )}
    {chips.length > 0 && <div className="chips add">
      {chips.map((c) => (
        <button key={c.label} className={`chip act${c.primary ? ' on' : ''}`} onClick={c.onAdd}>
          + {c.label}
        </button>
      ))}
    </div>}
  </>;
  if (split) {
    return <>
      {list && <section className="blk"><div className="form">{list}</div></section>}
      <div className="form onbadd">{add}</div>
    </>;
  }
  return <>{list ?? <div className="note">{empty}</div>}{add}</>;
}

function Block(props: {
  title: string; hint: React.ReactNode; children: React.ReactNode;
  onBack?: () => void; onNext: () => void; nextEnabled: boolean | undefined; nextLabel?: string;
  /** Плашка про весь шаг — над секцией, а не строкой списка. */
  top?: React.ReactNode;
  /** Секцию даёт содержимое само: список и «добавить» — отдельно (`Compact split`). */
  bare?: boolean;
}) {
  return (
    <>
      <h1 className="onbtitle">{props.title}<Tip>{props.hint}</Tip></h1>
      {props.top}
      {props.bare ? props.children : <section className="blk"><div className="form">{props.children}</div></section>}
      <div className="onbnav">
        {props.onBack && <button className="btn ghost" onClick={props.onBack}>Назад</button>}
        <button className="btn pri" disabled={!props.nextEnabled} onClick={props.onNext}>
          {props.nextLabel ?? 'Дальше'}
        </button>
      </div>
    </>
  );
}

/**
 * Первая цифра — момент ценности (PRD §4.11).
 *
 * Заведомо завышена: обязательств ещё нет. Плашка точности не мелкий серый
 * текст, а полноценный элемент рядом с числом — врать нельзя, но и ждать
 * конца онбординга ради награды тоже нельзя.
 */
function FirstNumber({ d, onNext, onAccounts }: { d: Draft; onNext: () => void; onAccounts: () => void }) {
  // Только тратимые счета: ни накопительный, ни кредитка в первую цифру не
  // входят, иначе она окажется завышенной — и человек это заметит первым же
  // сравнением с банковским приложением.
  const balance = d.accounts
    .filter((a) => a.kind !== 'credit_card' && liquidOf(a))
    .reduce((acc, a) => add(acc, money(a.balance)), ZERO);
  const income = d.incomes.reduce((a, i) => add(a, money(i.amount)), ZERO);
  const accountCount = d.accounts.filter((a) => a.name.trim()).length;
  const facts = draftFacts(d);
  /*
    Счетов нет — «0 ₽» и пустое «Учтено» читались как «у меня ноль денег»
    (владелец, 22.10.2026). Говорим, что это значит для расчёта, и даём
    вернуться: без остатков считать «можно тратить» не от чего.
  */
  if (d.accounts.length === 0) {
    return (
      <>
        <h1 className="onbtitle">Остатки на счетах не указаны</h1>
        <section className="blk"><div className="pw-plain">
          <p>Без них SKOKA не знает, сколько денег есть сейчас. Поэтому «Можно тратить» будет <b>0 ₽</b>,
            а платежи до прихода покажутся нехваткой — даже если деньги на карте есть.</p>
          <p>Хватит одной карты, с которой чаще всего тратишь: цифра станет настоящей сразу. Остальные счета
            можно добавить потом в «Деньгах».</p>
        </div></section>
        <div className="onbnav">
          <button className="btn ghost" onClick={onNext}>Дальше без счетов</button>
          <button className="btn pri" onClick={onAccounts}>Добавить счёт</button>
        </div>
      </>
    );
  }
  return (
    <>
      {/* Заголовок честный: это ещё не «можно потратить», а то, что на
          счетах. Обязательные платежи не учтены, и назвать эту сумму
          доступной значило бы пообещать деньги, которые уже обещаны другим. */}
      <h1 className="onbtitle">На указанных счетах
        <Tip>Поэтому это пока не «можно потратить»: платежи вычтутся, и цифра
          станет меньше — зато настоящей.</Tip>
      </h1>
      <div className="firstnum num">{formatMoney(balance)}</div>

      {/*
        Перечень фактов вместо процента точности (PRD §4.11).

        «Точность картины 45%» — оценка, которой не существует: доля от чего?
        Человек не может ни проверить её, ни понять, что делать, чтобы стало
        больше. Список того, что учтено и что осталось, отвечает на оба
        вопроса сразу.
      */}
      {facts.known.length > 0 && (
        <div className="acc" style={{ marginTop: 4 }}>
          <b>Учтено</b>
          <span>{facts.known.join(' · ')}</span>
        </div>
      )}
      {facts.missing.length > 0 && (
        <div className="acc">
          <b>Осталось уточнить</b>
          <span>{facts.missing.join(' · ')}</span>
        </div>
      )}
      <div className="onbnav">
        <button className="btn ghost onbback" onClick={onAccounts}>Назад</button>
        <button className="btn pri" onClick={onNext}>Добавить платежи · 90 сек</button>
      </div>
    </>
  );
}

/**
 * Вердикт: сходится ли картина (PRD §4.12).
 *
 * Считается тем же движком, что и потом в приложении, — иначе онбординг
 * пообещал бы одно, а первый же экран показал другое.
 */
function Verdict({ d, set, busy, err, onBack, onCommit, onFixBaseline, onStep }: {
  d: Draft;
  set: <K extends keyof Draft>(k: K, v: Draft[K]) => void;
  busy: boolean; err: string | null; onBack: () => void; onCommit: () => void;
  /** Вернуться к шагу, данных которого не хватило разбору. */
  onStep: (step: Step) => void;
  /** Вернуться к вопросу об обычных тратах прямо с финала. */
  onFixBaseline: () => void;
}) {
  const today = todayPd();
  // Один перевод черновика на все ответы: вердикт, главная задача и разбор ИИ
  // обязаны говорить об одной и той же картине.
  const e = useMemo(() => draftEntities(d, today), [d, today]);
  // Тем же движком, что и на главной: вердикт обязан показать ту цифру,
  // которую человек увидит сразу после сохранения.
  const now = useMemo(() => draftAvailable(d, today), [d, today]);
  const knownNeed = valueOr(e.baselines.need, ZERO);
  const cycle = computeCycleOutlook({
    available: now.available, days: now.daysToHorizon, needBaseline: knownNeed,
  });
  const v: VerdictResult = useMemo(() => computeVerdict({
    today,
    incomeStreams: e.incomeStreams,
    obligations: e.obligations,
    needBaseline: knownNeed,
    wantBaseline: valueOr(e.baselines.want, ZERO),
    // Долги нужны вердикту, чтобы отличить дорогой долг от ипотеки под
    // умеренный процент: гасить раньше подушки стоит только первый.
    loans: e.loans,
    goals: e.goals,
  }), [e, today, knownNeed]);

  /*
    Свободный остаток месяца и три варианта его деления.

    Считается тем же планом, что и после сохранения: показывать на финале
    одни числа, а на главной другие — верный способ потерять доверие на
    первом же экране.
  */
  const monthPlan = useMemo(() => computePlan({
    incomeStreams: e.incomeStreams,
    obligations: e.obligations,
    needBaseline: knownNeed,
    goals: e.goals,
    accounts: e.accounts,
    creditTerms: e.creditTerms,
    mode: d.planMode ?? DEFAULT_MODE,
    today,
  }), [e, knownNeed, d.planMode, today]);
  const surplus = add(monthPlan.amounts.want, monthPlan.amounts.save);
  const choice = useMemo(() => modeChoice({
    today,
    rest: surplus,
    goals: e.goals,
    remaining: Object.fromEntries(e.goals.map((g) => [g.id, g.monthlyContribution])),
  }), [today, surplus, e.goals]);

  // Главная задача называется здесь же: вердикт отвечает «сходится или нет»,
  // а человеку нужно ещё и «с чего начинать».
  //
  // Пока обычные траты неизвестны, главная задача — узнать их, а не копить
  // подушку. Иначе расчёт берёт за стоимость месяца сумму обязательных
  // платежей: у человека с одной годовой страховкой выходило, что месяц жизни
  // стоит 60 000 ₽ — число, взятое буквально из воздуха.
  // Тот же расчёт, что на главной: пока обычные траты неизвестны, главное —
  // узнать их (`unknown_need`), а не копить подушку от суммы одних платежей.
  const focus = useMemo(() => diagnose({
    today,
    incomeStreams: e.incomeStreams,
    obligations: e.obligations,
    needBaseline: knownNeed,
    needKnown: e.baselines.need.known,
    loans: e.loans,
    goals: e.goals,
  }), [e, today, knownNeed]);

  /**
   * Чего не хватает для полной картины.
   *
   * Это замена проценту точности: человеку показывают, что уточнить, а не
   * насколько он «неполон». Список пуст — значит, спрашивать больше нечего.
   */
  // Тот же расчёт, что на экране первой цифры: один список на оба места,
  // иначе они разойдутся и будут спорить друг с другом.
  const missing = useMemo(() => draftFacts(d).missing, [d]);

  const rec = v.scenarios.find((s) => s.recommended) ?? v.scenarios[0];

  /*
    Финал — одна цифра и один вопрос (03.11.2026).

    Прежде здесь было семь блоков: главная задача с подушкой, разбор моделью,
    «это уже оплачено», до зарплаты, со следующей зарплаты, рекомендация и
    выбор режима. Владелец: «перенасыщено и может испугать», а разбор без
    единой операции ничего не разбирает. Осталось то, что человек увидит на
    главной первым, — «Можно тратить» в той же карточке, — и вопрос, без
    которого эта цифра неверна. Цели, подушка и разбор предлагаются на
    главной, когда появятся траты.
  */
  const until = now.horizonDate ? formatDateDay(now.horizonDate) : null;
  return (
    <>
      <h1 className="onbtitle">{e.baselines.need.known && !cycle.covered ? 'До зарплаты впритык' : 'Всё готово'}</h1>

      <section className="gh onbgh">
        <h2 className="gh-title">{until ? `Можно тратить до ${until}` : 'Можно тратить'}</h2>
        <div className="gh-row"><div className="gh-sum num">{formatMoney(cycle.available)}</div></div>
        {cycle.days > 0 && <div className="gh-day num">≈ {formatMoney(cycle.perDay)} в день</div>}
        <div className="gh-sub">Платежи до зарплаты уже вычтены. Эта же цифра будет на главной.</div>
        {e.baselines.need.known && !cycle.covered && (
          <div className="gh-strip warn"><i aria-hidden="true" />
            <span>На повседневные нужды до зарплаты нужно около {formatMoney(cycle.livingNeed)} — не хватает {formatMoney(cycle.shortfall)}</span></div>
        )}
      </section>

      {/* «Это уже оплачено?» переехал к строкам платежей на шаге «Какие обязательные платежи?» (03.11.2026). */}

      {/* Месяц — одной строкой: расшифровка в (i), а не таблицей из пяти строк. */}
      <section className="blk">
        {e.baselines.need.known ? (
          <div className="row"><div className="rl">Каждый месяц свободно
            <Tip>Доход {formatMoney(v.income)} − платежи {formatMoney(v.obligationsMonthly)} −
              повседневные нужды {formatMoney(sub(v.needTotal, v.obligationsMonthly))}.
              Эти деньги — на свободные траты и цели.</Tip>
            <span className="rn">после платежей и повседневных нужд</span></div>
            <div className="rv num">{formatMoney(v.freeCash)}</div></div>
        ) : (
          <>
            <div className="row"><div className="rl">Каждый месяц после платежей
              <Tip>Это не свободные деньги: из этой суммы ещё живут месяц. Сколько
                уходит на повседневные нужды — пока неизвестно.</Tip></div>
              <div className="rv num">{formatMoney(sub(v.income, v.obligationsMonthly))}</div></div>
            <button className="btn ghost" onClick={onFixBaseline}>Указать расходы на повседневные нужды</button>
          </>
        )}
      </section>

      <p className="onbnext">Цели, подушку и разбор трат SKOKA предложит на главной, когда появятся первые записи.</p>

      {err && <div className="note warn">{err}</div>}
      <div className="onbnav">
        <button className="btn ghost" onClick={onBack}>Назад</button>
        <button className="btn pri" disabled={busy} onClick={onCommit}>Открыть SKOKA</button>
      </div>
    </>
  );
}

/**
 * Разбор настоящей моделью на финале настройки.
 *
 * Это и есть обещание продукта: человек вводит данные и получает разбор, а не
 * таблицу. Разбор идёт **до сохранения** — по черновику, — потому что
 * требовать регистрацию прежде, чем показать хоть какую-то пользу, значит
 * требовать доверия авансом.
 *
 * Два правила, унаследованные от советника и здесь не ослабленные:
 *
 * · числа в ответе проверяются по расчёту, и выдуманные не показываются;
 * · отказ модели не ломает экран — тот же ответ собирается расчётом и честно
 *   называется расчётом без разбора.
 */
/**
 * Куда вести, когда разбору не хватило данных: по словам модели («обычные
 * расходы», «доход», «обязательные платежи») — к шагу онбординга, где это
 * вводится. Поля для ответа здесь нет, и «ответь на вопрос» без кнопки был
 * тупиком (владелец, 22.10.2026).
 */
const GAP_STEPS: { test: RegExp; step: Step; label: string }[] = [
  { test: /обычн|повседнев|жизн|траты|расход/i, step: 'baseline', label: 'Указать обычные траты' },
  { test: /доход|зарплат|приход/i, step: 'income', label: 'Добавить доход' },
  { test: /платеж|платёж|кредит|аренд/i, step: 'obligations', label: 'Добавить платежи' },
  { test: /сч[её]т|остат|баланс/i, step: 'accounts', label: 'Добавить счёт' },
];
const gapSteps = (texts: string[]) => GAP_STEPS.filter((g) => texts.some((t) => g.test.test(t)));

function DraftAnalysis({ entities, today, onStep }: { entities: DraftEntities; today: PlainDate; onStep: (s: Step) => void }) {
  const [state, setState] = useState<
    | { kind: 'idle' }
    | { kind: 'thinking' }
    | { kind: 'answer'; answer: AdvisorAnswer }
    | { kind: 'question'; question: string }
    /** Модель не ответила: предлагаем повторить, а не шаблон вместо разбора. */
    | { kind: 'failed' }
  >({ kind: 'idle' });
  const [reply, setReply] = useState('');
  /** Согласие на передачу данных в модель. Заранее не отмечено. */
  const [agreed, setAgreed] = useState(false);
  /**
   * Начатый разбор: его ключ и заданные уточнения.
   *
   * Живёт столько же, сколько сам разбор. Без ключа ответ на уточнение
   * начинает новую работу: человек платит второй единицей за вопрос модели
   * к себе самому, а модель забывает ответ сразу, как его получила.
   */
  const analysis = useRef<{ id?: string; history: ClarifyTurn[] }>({ history: [] });

  const input = useMemo(() => ({
    today,
    // Подпись данных: по ней ответ признаётся устаревшим. Считается по тем же
    // сущностям, которыми считается разбор, — иначе правка суммы платежа
    // оставляла бы прежний ответ «свежим».
    stateVersion: draftVersion(entities),
    accounts: entities.accounts,
    creditTerms: entities.creditTerms,
    incomeStreams: entities.incomeStreams,
    obligations: entities.obligations,
    obligationsKnown: entities.obligationsKnown === true,
    goals: entities.goals,
    plannedExpenses: [],
    reserves: [],
    loans: entities.loans,
    needBaseline: entities.baselines.need,
    wantBaseline: entities.baselines.want,
  }), [entities, today]);

  /** Ход разбора: первый или продолжение с ответом человека. */
  const run = async (answer?: string): Promise<void> => {
    const asked = state.kind === 'question' ? state.question : null;
    const history = asked && answer
      ? [...analysis.current.history, { question: asked, answer }]
      : analysis.current.history;

    setState({ kind: 'thinking' });
    setReply('');
    if (!answer) track('onb_analysis');
    const out = await runAdvisor({
      context: advisorContext(input),
      tools: input,
      ...(answer ? { question: answer } : {}),
      ...(analysis.current.id ? { analysisId: analysis.current.id } : {}),
      history,
      // Данные могли измениться, пока шёл ответ: показывать его тогда нельзя.
      currentVersion: () => draftVersion(entities),
    });

    if (out.kind === 'stale') { setState({ kind: 'idle' }); return; }
    if (out.kind === 'failed') { setState({ kind: 'failed' }); return; }
    if (out.kind === 'question') {
      analysis.current = { id: out.analysisId, history };
      setState({ kind: 'question', question: out.question });
      return;
    }
    analysis.current = { history: [] };
    setState({ kind: 'answer', answer: out.answer });
  };

  return (
    <section className="blk">
      <div className="blkhead onb-sec"><span className="onb-sec-i" aria-hidden="true"><Icon name="sparkles" size={15} /></span><b>Разбор
        {state.kind === 'question' && (
          <Tip>Не знаешь точно — назови примерно. Пропустить тоже можно: картина
            уже посчитана, разбор станет менее точным.</Tip>
        )}</b>
        <span className="tag soft">бесплатно</span></div>

      {state.kind === 'idle' && (
        <>
          {/*
            Разбор — единственное место, где данные покидают устройство, и
            случается это **до** регистрации. Значит, и предупредить, и
            спросить согласие надо здесь: после отправки согласие уже ничего
            не значит. Отметка не стоит заранее — её ставит человек.
          */}
          {/* Коротко — что произойдёт; подробности о данных — в «i» у согласия
              (владелец, 22.10.2026: длинный текст о передаче данных стоял
              раньше самой кнопки). */}
          <div className="onb-lead">SKOKA посмотрит на картину целиком и скажет, что в ней главное и с чего начать. Регистрация не нужна.</div>
          <button className="btn pri" disabled={!agreed}
            onClick={async () => {
              await recordConsent({
                kind: 'ai_processing', documentVersion: DOC_VERSION,
                method: 'checkbox', granted: true,
              });
              void run();
            }}>
            Разобрать мою картину
          </button>
          <label className="check consent">
            <input type="checkbox" checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)} />
            {/* Возраст — здесь же: данные уходят в модель уже до регистрации (решение владельца 22.10.2026). */}
            <span>Мне есть 18 лет, даю согласие передать данные
              <Tip>Картина денег уйдёт на сервер и в языковую модель: счета, платежи,
                доходы и цели — без имени и почты. Всё остальное в приложении считается
                на твоём устройстве. Разбор делает модель: она ошибается, и её ответ —
                не индивидуальная финансовая рекомендация. Решение остаётся за тобой.</Tip>
            </span>
          </label>
        </>
      )}

      {state.kind === 'thinking' && <div className="note">Считаю по твоим данным…</div>}

      {state.kind === 'failed' && (
        <>
          <div className="note warn" style={{ marginTop: 0 }}>
            Не получилось сделать разбор: ИИ сейчас не ответил. Попробуй ещё раз.
          </div>
          <button className="btn pri" onClick={() => void run()}>Повторить</button>
        </>
      )}

      {/*
        Уточнение — середина разбора, а не его итог.

        Вопрос без поля ответа выглядел как результат: человеку предлагали
        ответить «позже в приложении», а начатый разбор туда не переносился.
        Первый бесплатный разбор обещан **с уточнениями** — значит, ответить
        нужно здесь и сейчас, и здесь же получить законченный вывод.
      */}
      {state.kind === 'question' && (
        <>
          <div className="li"><div className="nm"><b>{state.question}</b>
            <span>Ответь — и разбор закончится</span></div></div>
          <div className="askrow">
            <input
              className="askfield"
              value={reply}
              onChange={(ev) => setReply(ev.target.value)}
              placeholder="Ответ"
              aria-label="Ответ на уточнение"
              onKeyDown={(ev) => { if (ev.key === 'Enter' && reply.trim()) void run(reply.trim()); }} />
            <button className="btn inline pri" disabled={!reply.trim()}
              onClick={() => void run(reply.trim())}>Ответить</button>
          </div>
          <button className="btn ghost" onClick={() => void run('Не знаю')}>Пропустить вопрос</button>
        </>
      )}

      {/* Тот же вид ответа, что в окне «Спроси SKOKA». */}
      {state.kind === 'answer' && state.answer.insufficient && (() => {
        const a = state.answer;
        const steps = gapSteps([a.basis, ...a.limits.map(String)]);
        return (
          <div className="onb-gap">
            <b>Для разбора не хватает данных</b>
            {a.basis && <p>{a.basis}</p>}
            {steps.length > 0 && (
              <div className="onb-gap-acts">
                {steps.map((g) => <button key={g.step} className="btn" onClick={() => onStep(g.step)}>{g.label}</button>)}
              </div>
            )}
            <p className="onb-gap-note">Разбор не потрачен: заполни и нажми «Разобрать мою картину» ещё раз.</p>
          </div>
        );
      })()}
      {state.kind === 'answer' && !state.answer.insufficient && (() => {
        const view = chatAnswerOf(state.answer);
        return (
          <div className="chat-ans onb-ans">
            {'text' in view
              ? <p className="chat-text">{tidy(view.text, todayPd())}</p>
              : <AnswerBody answer={view.answer} today={todayPd()} />}
          </div>
        );
      })()}
    </section>
  );
}

/**
 * Подпись черновика для проверки устаревания.
 *
 * Считается по всем числам, которые участвуют в разборе. Пересчёт по количеству
 * записей был бы обманом: правка платежа с 8 000 на 90 000 ₽ не меняет их
 * количества, а меняет ответ целиком.
 */
function draftVersion(e: DraftEntities): string {
  const parts = [
    `obligationsKnown:${e.obligationsKnown === true}`,
    ...e.accounts.map((a) => `${a.id}:${a.balance}`),
    // Ритм целиком: правка второй даты многодатного дохода меняет картину
    // так же, как правка суммы.
    ...e.incomeStreams.map((i) => streamFingerprint(i)),
    ...e.obligations.map((o) => `${o.id}:${o.amount}:${o.schedule?.anchor ?? o.dayOfMonth}`),
    ...e.goals.map((g) => `${g.id}:${g.targetAmount}:${g.monthlyContribution}:${g.startDate ?? ''}`),
    ...e.loans.map((l) => `${l.id}:${l.principal}:${l.payment}`),
    JSON.stringify(e.baselines),
  ];
  return parts.join('|');
}

/**
 * Что уже названо и чего ещё нет — по текущему черновику.
 *
 * Список был написан словами и не менялся. Человек, вернувшийся назад после
 * того как завёл платежи, читал, что платежей у него нет: приложение
 * рассказывало ему про него же неправду и просило сделать сделанное.
 */
export function draftFacts(d: Draft): { known: string[]; missing: string[] } {
  const known: string[] = [];
  const missing: string[] = [];

  const accounts = d.accounts.filter((a) => a.name.trim()).length;
  if (accounts > 0) known.push(plural(accounts, 'счёт', 'счёта', 'счетов'));
  else missing.push('счета');

  const income = d.incomes.reduce((a, i) => add(a, money(i.amount)), ZERO);
  if (income > ZERO) known.push(`доход ${formatMoney(income)}`);
  else if (d.unknown.includes('income')) known.push('дохода пока нет — так и записано');
  else missing.push('доход');

  const payments = d.obligations.filter((o) => o.name.trim() && money(o.amount) > ZERO).length
    + d.loans.filter((l) => l.name.trim() && money(l.payment) > ZERO).length;
  if (payments > 0) known.push(plural(payments, 'платёж', 'платежа', 'платежей'));
  else if (d.obligationsKnown === true) known.push('обязательных платежей нет');
  else missing.push('обязательные платежи');

  // «Не знаю» — это ответ, а не пробел: переспрашивать его незачем.
  const need = draftBaselines(d).need;
  if (need.known) known.push(`расходы на повседневные нужды ${formatMoney(need.value)}`);
  else if (need.reason === 'user_unknown') known.push('расходы на повседневные нужды пока неизвестны');
  else missing.push('обычные траты на повседневные нужды');

  const goals = d.goals.filter((g) => g.name.trim() && money(g.target) > ZERO).length;
  if (goals > 0) known.push(plural(goals, 'цель', 'цели', 'целей'));

  return { known, missing };
}

const MONTHS_DAY = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
/** «10 сентября» — дата платежа без года: год и так текущий. */
function formatDateDay(d: string): string {
  const [, m, day] = d.split('-');
  return `${Number(day)} ${MONTHS_DAY[Number(m) - 1]}`;
}

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
function formatDateShort(d: string): string {
  const [y, m] = d.split('-');
  return `${MONTHS[Number(m) - 1]} ${y}`;
}
